import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderFieldSelection } from 'src/components/loop-visualizations/layout/header-menu/HeaderFieldSelection';
import { useHeaderLabel } from 'src/components/loop-visualizations/layout/header-menu/useHeaderMenuLabel';
import {
  type DisabledField,
  getDisabledFields,
  headerToCustomFieldId,
  headerToFieldValue,
} from 'src/components/loop-visualizations/layout/header-menu/utils';
import { headerToPropertySelector } from 'src/lib/utils/roadmapping/commonLayoutUtils';
import { useUpsertTimelineLayoutMutation } from '@/lib/mutations/roadmapping/useTimelineLayoutMutations';
import { useGetCustomFieldDetailsQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import { useItemTypeLevelQuery } from '@/lib/queries/common/useItemTypeLevelQuery';
import { useTimelineLayout } from '@/lib/queries/roadmapping/useTimelineLayout';
import { useVisualizationByType } from '@/lib/queries/roadmapping/useVisualization';
import { useViewContext } from '../../ViewContext';

export const TIMELINE_ROW_AXIS_LABEL_WIDTH = 48;

export const TimelineRowAxisLabel = ({
  scrollContainerRef,
  headerHeight,
  layout: layoutProp,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  headerHeight: number;
  layout?: TimelineLayoutDTO;
}) => {
  const { t } = useTranslation();
  const { viewId, isPublishedMode } = useViewContext();
  const [open, setOpen] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.target.clientHeight);
      }
    });

    resizeObserver.observe(element);
    setContainerHeight(element.clientHeight);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  const { data: visualization } = useVisualizationByType(viewId, 'timeline', {
    enabled: !isPublishedMode,
  });
  const visualizationId = visualization?.visualizationId ?? 0;
  const { data: layoutData } = useTimelineLayout(viewId, visualizationId);
  const { mutate: mutateLayout } = useUpsertTimelineLayoutMutation(viewId, visualizationId);

  const effectiveLayout = isPublishedMode && layoutProp ? layoutProp : layoutData;

  const headerSelector = effectiveLayout?.header1
    ? headerToPropertySelector(effectiveLayout.header1)
    : null;
  const subheaderSelector = effectiveLayout?.header2
    ? headerToPropertySelector(effectiveLayout.header2)
    : null;

  const rowAxisLabel = useHeaderLabel(
    subheaderSelector ? subheaderSelector : headerSelector ? headerSelector : null,
  );

  const onRowHeaderChange = (header: string | null) => {
    if (!header) return;

    if (subheaderSelector) {
      mutateLayout({ header2: header });
    } else {
      mutateLayout({ header1: header, header2: null });
    }
    setOpen(false);
  };

  const onGroupRowsHeaderChange = (header: string | null) => {
    if (header === null && subheaderSelector && layoutData?.header2) {
      mutateLayout({ header1: layoutData.header2, header2: null });
    } else if (header && subheaderSelector) {
      mutateLayout({ header1: header });
    } else if (header && !subheaderSelector) {
      mutateLayout({ header1: header, header2: headerSelector ? layoutData?.header1 : null });
    }
    setOpen(false);
  };

  const { level: layoutLevel } =
    useItemTypeLevelQuery({ levelId: effectiveLayout?.itemLevelId ?? 0, enabled: !isPublishedMode })
      .data || {};

  const disabledMainFields: Array<DisabledField> = getDisabledFields(
    subheaderSelector && headerSelector ? [headerSelector] : [],
    t,
    layoutLevel,
  );
  const disabledSubFields: Array<DisabledField> = getDisabledFields(
    subheaderSelector ? [subheaderSelector] : headerSelector ? [headerSelector] : [],
    t,
    layoutLevel,
  );
  const mainHeaderFieldId = headerToCustomFieldId(headerSelector);
  const subHeaderFieldId = headerToCustomFieldId(subheaderSelector);

  const { data: mainHeaderField } = useGetCustomFieldDetailsQuery({
    id: mainHeaderFieldId,
  });
  const { data: subHeaderField } = useGetCustomFieldDetailsQuery({
    id: subHeaderFieldId,
  });

  const rowHeaderValue = subheaderSelector
    ? headerToFieldValue(subheaderSelector) || subHeaderField?.id.toString()
    : headerToFieldValue(headerSelector) || mainHeaderField?.id.toString();
  const groupRowsByValue = subheaderSelector
    ? headerToFieldValue(headerSelector) || mainHeaderField?.id.toString()
    : undefined;

  return (
    <div
      className="flex z-10 flex-col sticky top-0 left-0 px-2 h-full bg-bg-surface-base"
      style={{
        height: containerHeight || 0,
        width: TIMELINE_ROW_AXIS_LABEL_WIDTH,
      }}
    >
      <div className="bg-bg-surface-base z-5 top-0 sticky" style={{ minHeight: headerHeight }} />
      {isPublishedMode ? (
        <div className="flex items-center justify-center h-full w-full rounded-md border-[1.5px] tracking-[1px] bg-bg-neutral">
          <span className="uppercase text-[0.75rem] -rotate-90 w-max whitespace-nowrap font-normal">
            {rowAxisLabel}
          </span>
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger className="h-full w-full">
            <div className="flex items-center justify-center h-full w-full rounded-md border-[1.5px] tracking-[1px] cursor-pointer bg-bg-neutral">
              <span className="uppercase text-[0.75rem] -rotate-90 w-max whitespace-nowrap font-normal">
                {rowAxisLabel}
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            alignOffset={120}
            align="start"
            className="flex flex-col px-4! gap-2"
          >
            <span className="font-semibold text-sm  text-text-neutral-secondary">
              {t('roadmapping.layout.formatPanel.row.headers')}
            </span>
            <HeaderFieldSelection
              inputId="row-header-field-selection"
              onValueSelected={onRowHeaderChange}
              value={rowHeaderValue}
              disabledFields={disabledMainFields}
              includeDateHeader={false}
            />
            <span className="font-semibold text-sm  text-text-neutral-secondary">
              {t('roadmapping.layout.formatPanel.row.headers.group.by')}
            </span>
            <HeaderFieldSelection
              inputId="group-rows-header-field-selection"
              onValueSelected={onGroupRowsHeaderChange}
              value={groupRowsByValue}
              disabledFields={disabledSubFields}
              includeDateHeader={false}
              isClearable
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
